import type { OnChanges, SimpleChanges, AfterViewInit } from '@angular/core';
import { Component, ChangeDetectionStrategy, Input, ViewChild, ElementRef } from '@angular/core';
import { environment } from '@env/environment';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { RocosClientService, RocosSdkClientService } from '@shared/services';
import { WidgetBaseComponent } from '../shared/widget-base';
import type { IRocosSDKConfigToken } from '@dronedeploy/rocos-js-sdk';
import type { Observable } from 'rxjs';
import { filter, from, map, shareReplay, tap } from 'rxjs';

@UntilDestroy()
@Component({
  selector: 'app-widget-web-component',
  templateUrl: './widget-web-component.component.html',
  styleUrls: ['./widget-web-component.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WidgetWebComponentComponent extends WidgetBaseComponent implements OnChanges, AfterViewInit {
  // Shared across all widget instances so the same projectId only triggers one HTTP request.
  private static readonly projectOriginCache = new Map<string, Observable<{ lat: number; lon: number; alt: number }>>();

  @Input() public elementName: string;
  @Input() public componentConfig: Record<string, any>;
  @Input() public projectId: string;
  @Input() public callsign: string;
  @Input() public params: Readonly<Record<string, string | string[]>> = Object.freeze({});

  @ViewChild('placeholder') public placeholder: ElementRef;

  private token: string;
  private customElement: HTMLElement;
  private projectOrigin: { lat: number; lon: number; alt: number } | undefined;

  constructor(private clientService: RocosClientService, private rocosSdkClientService: RocosSdkClientService) {
    super();

    this.theme = 'none';
    this.clientService.token$.pipe(untilDestroyed(this)).subscribe((token) => {
      this.token = token;
      this.createOrUpdateElement();
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['projectId']?.currentValue) {
      this.projectOrigin = undefined;
      this.fetchProjectOrigin(changes['projectId'].currentValue);
      return; // createOrUpdateElement called by fetchProjectOrigin once origin resolves
    }
    if (changes['elementName'] || changes['componentConfig'] || changes['callsign'] || changes['params']) {
      this.createOrUpdateElement();
    }
  }

  ngAfterViewInit() {
    if (this.elementName) {
      this.createOrUpdateElement();
    }
  }

  private get sdkOptions(): IRocosSDKConfigToken {
    return {
      url: environment.api.url.replace('https://', ''),
      token: this.token,
      transport: environment.api.transport,
    };
  }

  private fetchProjectOrigin(projectId: string): void {
    if (!WidgetWebComponentComponent.projectOriginCache.has(projectId)) {
      WidgetWebComponentComponent.projectOriginCache.set(
        projectId,
        from(this.rocosSdkClientService.client.getProjectService().info(projectId)).pipe(
          filter(Boolean),
          map((info) => ({ lat: info.location.lat, lon: info.location.lng, alt: 0 })),
          // Cache so all widgets on the same project share one HTTP request.
          // Evict on error so a future widget can retry rather than replaying the failure.
          tap({ error: () => WidgetWebComponentComponent.projectOriginCache.delete(projectId) }),
          shareReplay(1),
        ),
      );
    }
    WidgetWebComponentComponent.projectOriginCache
      .get(projectId)
      .pipe(untilDestroyed(this))
      .subscribe((origin) => {
        this.projectOrigin = origin;
        this.createOrUpdateElement();
      });
  }

  private buildConfig(): Record<string, any> {
    return {
      ...(this.projectOrigin && { origin: this.projectOrigin }),
      tilesUrl: environment.droneDeploy?.tilesApi,
      ...this.componentConfig,
      // Always sourced from app environment / route — not overridable via dashboard config
      mapBoxToken: environment.mapbox.accessToken,
      googleMapsApiKey: environment.google.mapsApiKey,
      params: this.params,
    };
  }

  private createOrUpdateElement() {
    if (!this.placeholder || !this.elementName) return;

    if (this.customElement && this.customElement.tagName !== this.elementName.toUpperCase()) {
      this.customElement.remove();
      this.customElement = null;
    }

    if (!this.customElement) {
      this.customElement = document.createElement(this.elementName);
    }

    const el = this.customElement;

    el.setAttribute('sdk', JSON.stringify(this.sdkOptions));
    el.setAttribute('callsign', this.callsign);
    el.setAttribute('projectid', this.projectId);
    el.setAttribute('config', JSON.stringify(this.buildConfig()));

    this.placeholder.nativeElement.replaceChildren(el);
  }
}
